import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { actions } from '../../TransfersState'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { CardItemDetails } from '../../components/CardItemDetails/CardItemDetails'
import * as S from './TransferPageDetails.styles'
import { IconButton, Typography } from '@mui/material'
import TableMui from 'src/components/TableMui'
import UploadIcon from '@mui/icons-material/Upload'
import DownloadIcon from '@mui/icons-material/Download'
import { capitalizeString } from 'src/utils/capitalize'

interface Params {
  idPage: string
}

export const TransferPageDetails = () => {
  const { idPage } = useParams<Params>()

  const history = useHistory()

  const dispatch = useDispatch()
  const { transferPageDetails } = useSelector((state) => state.transfers)

  const [preventDoubleClick, setPreventDoubleClick] = useState(false)

  useEffect(() => {
    if (idPage) {
      dispatch(actions.getTranferPageDetails(Number(idPage)))
    }
  }, [dispatch, idPage])

  const goBack = () => {
    if (transferPageDetails) {
      const { idList } = transferPageDetails
      dispatch(actions.clearProperty('transferPageDetails'))
      history.push(`/transfers/manage/${idList}/pages`)
    } else {
      window.history.back()
    }
  }

  const handleIssueNfe = (idCage: number) => {
    if (preventDoubleClick) {
      return
    }

    setPreventDoubleClick(true)

    dispatch(actions.issueTransferListPageCageInvoice({
      idCage,
      setPreventDoubleClick,
    }))
  }

  const handleDownloadNfe = (idCage: number) => {
    if (preventDoubleClick) {
      return
    }

    setPreventDoubleClick(true)

    dispatch(actions.downloadTransferListPageCageInvoice({
      idCage,
      setPreventDoubleClick,
    }))
  }

  const resolveCageTableRows = useMemo(() => transferPageDetails?.cages.map(({
    idCage,
    address,
    status,
    orderCode,
    invoiceAccessKey,
    nfeStatus,
    nfeStatusDescription,
  }) => ({
    id: idCage,
    address,
    status: capitalizeString(status),
    orderCode: orderCode || '-',
    invoiceAccessKey: invoiceAccessKey || '-',
    nfeStatus: nfeStatus || '-',
    nfeStatusDescription: nfeStatusDescription || '-',
    issue: (
      <IconButton
        onClick={(event) => {
          event.stopPropagation()
          handleIssueNfe(idCage)
        }}
        disabled={preventDoubleClick}
        data-testid='issue-nfe-button-test-id'
      >
        <UploadIcon
          sx={{ cursor: 'pointer', color: preventDoubleClick ? 'grey' : 'green' }}
        />
      </IconButton>
    ),
    download: (
      <IconButton
        onClick={(event) => {
          event.stopPropagation()
          handleDownloadNfe(idCage)
        }}
        disabled={preventDoubleClick}
        data-testid='download-nfe-button-test-id'
      >
        <DownloadIcon
          sx={{ cursor: 'pointer', color: preventDoubleClick ? 'grey' : 'blue' }}
        />
      </IconButton>
    ),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [preventDoubleClick, transferPageDetails?.cages])

  return (
    <S.Wrapper>
      <S.CustomButton
        title='Voltar'
        type='button'
        bgColor='blue'
        hoverColor='darkBlueHover'
        letterColor='white'
        onClick={goBack}
        data-testid='back-button-test-id'
      />
      <Typography variant='h5' fontWeight='bold' sx={{ alignSelf: 'center' }}>Detalhes de Página de Transferência</Typography>
      {transferPageDetails
      && (
        <>
          <S.GridPageUserInfo>
            <S.CustomTextField
              name='userStarted'
              id='userStarted'
              label='Iniciada por'
              type='text'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                readOnly: true,
                'data-testid': 'page-user-started-test-id',
              }}
              value={transferPageDetails.userStarted || '-'}
            />
            <S.CustomTextField
              name='startedAt'
              id='startedAt'
              label='Iniciada em'
              type='text'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                readOnly: true,
                'data-testid': 'page-started-at-test-id',
              }}
              value={transferPageDetails.startedAt || '-'}
            />
            <S.CustomTextField
              name='userFinished'
              id='userFinished'
              label='Finalizada por'
              type='text'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                readOnly: true,
                'data-testid': 'page-user-finished-test-id',
              }}
              value={transferPageDetails.userFinished || '-'}
            />
            <S.CustomTextField
              name='finishedAt'
              id='finishedAt'
              label='Finalizada em'
              type='text'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                readOnly: true,
                'data-testid': 'page-finished-at-test-id',
              }}
              value={transferPageDetails.finishedAt || '-'}
            />
          </S.GridPageUserInfo>
          <S.GridPageInfo>
            <S.CustomTextField
              name='totalItems'
              id='totalItems'
              label='Qtd. de itens'
              type='text'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                readOnly: true,
                'data-testid': 'page-total-items-test-id',
              }}
              value={transferPageDetails.totalItems}
            />
            <S.CustomTextField
              name='totalSkus'
              id='totalSkus'
              label='Qtd. de SKUs'
              type='text'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                readOnly: true,
                'data-testid': 'page-total-skus-test-id',
              }}
              value={transferPageDetails.totalSkus}
            />
            <S.CustomTextField
              name='totalCollectedQuantity'
              id='totalCollectedQuantity'
              label='Qtd. total coletada'
              type='text'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                readOnly: true,
                'data-testid': 'page-total-collected-quantity-test-id',
              }}
              value={transferPageDetails.totalCollectedQuantity}
            />
            <S.CustomTextField
              name='totalRestockedQuantity'
              id='totalRestockedQuantity'
              label='Qtd. total reposta'
              type='text'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                readOnly: true,
                'data-testid': 'page-total-restocked-quantity-test-id',
              }}
              value={transferPageDetails.totalRestockedQuantity}
            />
            <S.CustomTextField
              name='status'
              id='status'
              label='Status da página'
              type='text'
              InputLabelProps={{ shrink: true }}
              inputProps={{
                readOnly: true,
                'data-testid': 'page-status-test-id',
              }}
              value={transferPageDetails.status}
            />
          </S.GridPageInfo>
          <Typography
            variant='h6'
            sx={{
              marginTop: '.5rem',
              alignSelf: 'center',
              backgroundColor: '#58c2ff',
              padding: '0rem 1rem 0rem 1rem',
              borderRadius: '5px 5px 0 0',
            }}
          >
            GAIOLAS
          </Typography>
          <TableMui
            columns={[
              { id: 'address', label: 'Endereço', testId: 'cage-address-test-id' },
              { id: 'status', label: 'Status', testId: 'cage-status-test-id' },
              { id: 'orderCode', label: 'Código do pedido', testId: 'cage-order-code-test-id' },
              { id: 'invoiceAccessKey', label: 'NFe', testId: 'cage-invoice-access-key-test-id' },
              { id: 'nfeStatus', label: 'Status NFe', testId: 'cage-nfe-status-test-id' },
              { id: 'nfeStatusDescription', label: 'Descrição do status NFe', testId: 'cage-nfe-status-description-test-id' },
              { id: 'issue', label: 'Emitir', testId: 'cage-issue-test-id' },
              { id: 'download', label: 'Baixar', testId: 'cage-download-test-id' },
            ]}
            emptyLabel='Nenhuma gaiola para exibir'
            rows={resolveCageTableRows}
            rowCount={resolveCageTableRows?.length}
            enablePagination
            rowsPerPageLimit={5}
            maxHeight='60vh'
            testId='table-page-cages-details-test-id'
          />
          {transferPageDetails?.items.map((item) => (
            <CardItemDetails key={item.idItem} pageItemDetails={item} />
          ))}
        </>
      )}
    </S.Wrapper>
  )
}
